import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { UserSearchResultExternal } from '@askporter/client-grieg-lyric';
import { AvatarSize, BaseAutocomplete, RevampedIconSize, RevampedIcon, UserAvatar } from '@askporter/component-library';
import { useDebounce, fullName, isAnonymousUser } from '@askporter/utils';
import doUserSearch from '../../UserAutocomplete/utils/doUserSearch';
import { UserAutocompleteProps } from './types';

type SingleUserAutocompleteProps = {
  /**
   * The selected user for the autocomplete component
   */
  value: UserSearchResultExternal;
  /**
   * Whether the autocomplete allows multiple users to be selected
   */
  isMultiple?: false;
  /**
   * The function to call when the selected value changes (e.g. A user option is selected)
   */
  onChange: (user: UserSearchResultExternal) => void;
  /**
   * Whether to show the label
   */
  showLabel?: boolean;
  clearable?: boolean;
  forcePopupIcon?: boolean;
} & UserAutocompleteProps;

/**
 * An autocomplete component for selecting a single user for a list of options with infinite scroll,
 * it hits `users/search` endpoint to get the list of users
 */
export const RevampedSingleUserAutocomplete = ({
  idPrefix,
  label,
  disabled,
  filter = {},
  error = false,
  helperText,
  showUser = () => true,
  onInputChange,
  readOnly = false,
  readOnlyText = undefined,
  t,
  pageSize,
  value,
  onChange,
  onBlur,
  placeholder,
  optionsListAddAction,
  topOfListAdornment,
  showAvatar = true,
  showLabel = true,
  clearable = false,
  forcePopupIcon = true,
}: SingleUserAutocompleteProps): JSX.Element => {
  const [userValue, setUserValue] = useState<SingleUserAutocompleteProps['value']>(null);
  React.useEffect(() => {
    // A workaround to avoid re-renders caused by passing the value directly to the autocomplete component
    setUserValue(value);
  }, [JSON.stringify(value)]);

  const [inputValue, setInputValue] = useState('');
  const userKeyword = useDebounce<string>(inputValue, 1000);
  const search = { freeText: userKeyword, filter, pageSize };
  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, status } = useInfiniteQuery({
    queryKey: [`users/search`, { search }],
    queryFn: async ({ pageParam }) => {
      const response = await doUserSearch({
        ...search,
        page: pageParam,
      });
      return { response, nextPage: pageParam + 1 };
    },
    initialPageParam: 1,
    enabled: userKeyword === '' || userKeyword?.length > 2,
    getNextPageParam: (data) => {
      return data?.response?.summary?.totalPages >= data?.nextPage ? data?.nextPage : undefined;
    },
  });

  const getName = (user: UserSearchResultExternal) => {
    const isAnonymous = isAnonymousUser(user);

    return fullName(isAnonymous ? t('ns.common:people:anonymous_user') : user.givenName, user?.familyName);
  };

  const opts: UserSearchResultExternal[] =
    data?.pages && data?.pages.length > 0
      ? data.pages.reduce((acc, itm) => acc.concat([...itm.response.results.filter((user) => showUser(user))]), [])
      : [];

  const additionalOptions: UserSearchResultExternal[] = value ? [value as UserSearchResultExternal] : [];
  // Todo: can this logic be moved into base autocomplete?
  const selectedOptionsUids = additionalOptions.map((option) => option.uid);
  const filteredOptions = opts.filter((option) => !selectedOptionsUids.includes(option.uid));
  const options: UserSearchResultExternal[] = [...additionalOptions, ...filteredOptions];

  return (
    <BaseAutocomplete
      multiple={false}
      t={t}
      idPrefix={idPrefix}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      options={options}
      value={userValue}
      mapOptionToValue={(user: UserSearchResultExternal) => {
        return {
          uid: user.uid,
          label: getName(user),
          avatar: user?.profilePhoto?.filePath,
        };
      }}
      onChange={onChange}
      onBlur={onBlur}
      inputValue={inputValue}
      onInputChange={(_: React.SyntheticEvent, newInputValue: string) => {
        onInputChange && onInputChange(newInputValue);
        setInputValue(newInputValue);
      }}
      loading={status === 'pending' || isFetchingNextPage}
      onScrollToBottom={() => {
        if (hasNextPage) fetchNextPage();
      }}
      readOnly={readOnly}
      readOnlyText={readOnlyText}
      startAdornment={
        !showAvatar ? null : value && inputValue === getName(value) ? (
          <UserAvatar
            givenName={value.givenName}
            familyName={value.familyName}
            imagePath={value?.profilePhoto?.filePath}
            avatarSize={AvatarSize.XS}
          />
        ) : (
          <RevampedIcon fullPath="/media/icons/design-system/user/20px.svg" iconSize={RevampedIconSize.SM} />
        )
      }
      isListAvatarVisible={showAvatar}
      optionsListAddAction={optionsListAddAction}
      topOfListAdornment={topOfListAdornment}
      error={error}
      helperText={helperText}
      showLabel={showLabel}
      clearable={clearable}
      forcePopupIcon={forcePopupIcon}
    />
  );
};
